import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { ProjectDetail, ProjectSummary } from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { prepareUpload, photoName } from "./images";
import { clearInbox, flushUploads, isOffline, listInbox, listOutbox, listUploads, queueUpload, QUEUE_EVENT, removeRequest, removeUpload, type QueuedRequest, type QueuedUpload, type SharedItem } from "./offlineQueue";
import { Hero, Icon, Message } from "./ui";
import { folderPath, readLast, writeLast } from "./MobileApp";

/* Offline-Warteschlange, „Teilen an HandVero“ und Schnellablage für die Handy-App. */

interface Place {
  projectId: string;
  projectName: string;
  folderId: string;
  folderPath: string;
}

/** Datei hochladen; ohne Verbindung wird sie auf dem Gerät vorgemerkt. Gibt zurück, ob sie schon auf dem Server ist. */
export async function sendOrQueue(place: Place, raw: File, fromCamera: boolean): Promise<"sent" | "queued"> {
  const prepared = await prepareUpload(raw, fromCamera);
  const name = prepared.file.name;
  const queue = () => queueUpload({ ...place, name, file: prepared.file, thumb: prepared.thumb });
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    await queue();
    return "queued";
  }
  const form = new FormData();
  if (prepared.thumb) form.append("thumb", prepared.thumb, "vorschau.jpg");
  form.append("file", prepared.file, name);
  try {
    await upload(`/api/folders/${place.folderId}/files`, form);
    writeLast(place);
    return "sent";
  } catch (err) {
    if (!isOffline(err)) throw err;
    await queue();
    return "queued";
  }
}

/** Zeigt wartende Dateien und lädt sie hoch, sobald wieder Verbindung da ist. */
export function QueueBanner() {
  const [items, setItems] = useState<QueuedUpload[]>([]);
  const [reqs, setReqs] = useState<QueuedRequest[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);

  const refresh = useCallback(() => {
    void listUploads().then(setItems);
    void listOutbox().then(setReqs);
  }, []);
  const flush = useCallback(async () => {
    if ((await listUploads()).filter((x) => !x.error).length + (await listOutbox()).filter((x) => !x.error).length === 0) return;
    setBusy(true);
    const r = await flushUploads();
    setBusy(false);
    if (r.sent) setDone(r.sent);
    refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
    void flush();
    const again = () => void flush();
    window.addEventListener("online", again);
    window.addEventListener(QUEUE_EVENT, refresh);
    document.addEventListener("visibilitychange", again);
    const timer = window.setInterval(again, 30000);
    return () => {
      window.removeEventListener("online", again);
      window.removeEventListener(QUEUE_EVENT, refresh);
      document.removeEventListener("visibilitychange", again);
      window.clearInterval(timer);
    };
  }, [flush, refresh]);

  useEffect(() => {
    if (!done) return;
    const t = window.setTimeout(() => setDone(0), 4000);
    return () => window.clearTimeout(t);
  }, [done]);

  const waiting = [...items, ...reqs].filter((x) => !x.error);
  const failed = [...items.filter((x) => x.error), ...reqs.filter((x) => x.error)];
  if (!waiting.length && !failed.length && !done) return null;

  return (
    <div className="qbanner" role="status" aria-live="polite">
      {waiting.length > 0 && (
        <p>
          <b>{waiting.length === 1 ? "1 Eintrag wartet" : `${waiting.length} Einträge warten`} auf Verbindung.</b>{" "}
          <span>Sie sind sicher auf Ihrem Gerät und werden automatisch hochgeladen. Bitte lassen Sie die App dafür kurz geöffnet.</span>{" "}
          <button className="linklike" disabled={busy} onClick={() => void flush()}>
            {busy ? "Wird versucht …" : "Jetzt versuchen"}
          </button>
        </p>
      )}
      {done > 0 && waiting.length === 0 && <p><b>{done === 1 ? "1 wartende Datei ist" : `${done} wartende Dateien sind`} jetzt gespeichert.</b></p>}
      {failed.map((f) => (
        <p key={`${"name" in f ? "u" : "r"}${f.id}`} className="qerr">
          <b>{"name" in f ? f.name : f.label}</b> konnte nicht gespeichert werden: {f.error}{" "}
          <button className="linklike" onClick={() => void ("name" in f ? removeUpload(f.id!) : removeRequest(f.id!))}>
            Entfernen
          </button>
        </p>
      ))}
    </div>
  );
}

/** Kamera-Knopf auf der Startseite: Foto geht ohne weiteres Antippen in den zuletzt benutzten Ordner. */
export function QuickPhoto() {
  const [last, setLast] = useState<Place | null>(() => readLast());
  const [state, setState] = useState<"" | "busy" | "sent" | "queued" | "error">("");
  const [error, setError] = useState("");
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const t = state === "sent" || state === "queued" ? window.setTimeout(() => setState(""), 3500) : undefined;
    return () => window.clearTimeout(t);
  }, [state]);
  if (!last) return null;

  async function pick(list: FileList | null) {
    if (!list?.length || !last) return;
    setState("busy");
    try {
      let queued = false;
      for (const f of Array.from(list)) queued = (await sendOrQueue(last, f, true)) === "queued" || queued;
      setLast(readLast());
      setState(queued ? "queued" : "sent");
    } catch (err) {
      setError(errorText(err));
      setState("error");
    }
  }

  return (
    <div className="quick">
      <button className="app-btn" disabled={state === "busy"} onClick={() => ref.current?.click()}>
        <Icon name="camera" /> {state === "busy" ? "Wird gespeichert …" : "Schnellfoto"}
      </button>
      <small>
        landet in „{last.folderPath}“ ({last.projectName})
      </small>
      {state === "sent" && <small className="ok">Gespeichert.</small>}
      {state === "queued" && <small className="ok">Kein Netz: Foto wartet auf dem Gerät und wird später hochgeladen.</small>}
      {state === "error" && <small className="qerr">{error}</small>}
      <input ref={ref} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void pick(e.target.files); e.target.value = ""; }} />
    </div>
  );
}

/** Ziel der Android-Funktion „Teilen“: geteilte Fotos und PDFs in einen Ordner legen. */
export function ShareTargetPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<SharedItem[] | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [projectId, setProjectId] = useState(readLast()?.projectId ?? "");
  const [folderId, setFolderId] = useState(readLast()?.folderId ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void listInbox().then(setItems);
    api.get<ProjectSummary[]>("/api/projects").then(setProjects, (err) => setError(errorText(err)));
  }, []);
  useEffect(() => {
    if (!projectId) return setDetail(null);
    api.get<ProjectDetail>(`/api/projects/${projectId}`).then(setDetail, (err) => setError(errorText(err)));
  }, [projectId]);

  async function save() {
    if (!items?.length || !detail || !folderId) return;
    const folder = detail.folders.find((f) => f.id === folderId);
    if (!folder) return;
    const place: Place = {
      projectId,
      projectName: detail.name,
      folderId,
      folderPath: folderPath(detail.folders, folderId).map((f) => f.name).join(" › "),
    };
    setBusy(true);
    setError("");
    try {
      for (const it of items) {
        const file = new File([it.blob], it.name || photoName(), { type: it.type });
        await sendOrQueue(place, file, false);
      }
      await clearInbox();
      writeLast(place);
      navigate(`/app/p/${projectId}/f/${folderId}`, { replace: true });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <>
      <Hero kicker="Teilen an HandVero" title="Wohin damit?" back="/app" />
      <main className="app-main">
        <Message>{error}</Message>
        {items && items.length === 0 && (
          <p className="app-empty">
            Hier ist nichts angekommen. Wählen Sie in der Galerie Ihres Handys Fotos aus, tippen Sie auf „Teilen“ und dann auf „HandVero“. Das klappt
            auf Android, wenn HandVero zum Startbildschirm hinzugefügt ist.{" "}
            <Link to="/app">Zurück zu den Projekten</Link>
          </p>
        )}
        {items && items.length > 0 && (
          <form className="app-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
            <p>{items.length === 1 ? "1 Datei" : `${items.length} Dateien`} zum Ablegen.</p>
            <label>
              Projekt
              <select value={projectId} onChange={(e) => { setProjectId(e.target.value); setFolderId(""); }} required>
                <option value="">Bitte wählen</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Ordner
              <select value={folderId} onChange={(e) => setFolderId(e.target.value)} required disabled={!detail}>
                <option value="">Bitte wählen</option>
                {(detail?.folders ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {folderPath(detail!.folders, f.id).map((x) => x.name).join(" › ")}
                  </option>
                ))}
              </select>
            </label>
            <button className="app-btn" disabled={busy || !folderId}>
              {busy ? "Wird gespeichert …" : "Hier ablegen"}
            </button>
          </form>
        )}
      </main>
    </>
  );
}
